import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, isDevMode, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  forkJoin,
  interval,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from 'rxjs';
import {
  catchError,
  finalize,
  flatMap,
  map,
  mergeMap,
  tap,
  timeInterval
} from 'rxjs/operators';

import { Router } from '@angular/router';
import { EndPointService } from 'src/app/core/services/end-point.service';
import { TwoFactorAuthenticationService } from '../../modules/authentication/2fa/services/2fa.service';
import { ManagerType } from '../../shared/personnel-files/models/ManagerType';
import { AuthorizationBody, ITokenRequestData } from '../models/auth.interfaces';
import {
  CoupledEmployee,
  DelegatedTeam,
  StudentRoleSettings,
  UserLevelEnum,
} from '../models/UserData';
import { UserService } from './user.service';

@Injectable()
export class AuthService implements OnDestroy {
  public tokenStorageKey = 'Progreso.authorizationData';
  private authResponse: AuthorizationBody;
  private _subscriptions: Subscription[] = [];
  private readonly publicEmbeddedRouteFragments = [
    '/e2r/candidate-form',
    '/e2r/candidate-login',
    '/e2r/candidate-forgot-password',
    '/e2r/candidate-set-password',
    '/e2r/signin-with-linkedin',
  ];

  private loggedIn$: Subject<boolean> = new BehaviorSubject<boolean>(false);

  public isLoggedIn(): Observable<boolean> {
    return this.loggedIn$.asObservable();
  }

  constructor(
    private http: HttpClient,
    private _router: Router,
    private _userService: UserService,
    private _endPointService: EndPointService,
    private _twoFactorAuthenticationService: TwoFactorAuthenticationService
  ) {
    const intervalTime = 1000 * 60 * 5;

    this._subscriptions.push(
      interval(intervalTime).pipe(timeInterval(), mergeMap(() => this.keepAlive())).subscribe()
    );
  }

  public getToken(): Observable<AuthorizationBody> {
    this.getTokenFromStorageAndSetToMemory();
    if (
      typeof this.authResponse !== 'undefined'
    ) {
      this.loggedIn$.next(true);
      return of(this.authResponse);
    } else {
      const tokenData: ITokenRequestData = {
        loginKey: null,
        securityCode: null,
      };

      this.authResponse = new AuthorizationBody();
      this.adornKeyAndCodeFromStorage(tokenData);

      if (!tokenData.loginKey || !tokenData.securityCode) {
        this.authResponse = undefined;
        this.loggedIn$.next(false);
        return of(undefined);
      }

      return this.getAuthorizationToken(tokenData).pipe(
        tap((data: AuthorizationBody) => {
          if (this.tokenRequestMatchesCurrentCredentials(tokenData)) {
            this.setTokenInStorageAndMemory(data);
            this.loggedIn$.next(true);
          }
          return this.authResponse;
        }),
        catchError((error) => {
          if (this.tokenRequestMatchesCurrentCredentials(tokenData)) {
            this.clearStorage();
          }
          return throwError(() => error);
        })
      );
    }
  }

  public isAuthenticated(): boolean {
    return (
      this.authResponse !== null &&
      this.authResponse !== undefined &&
      this.authResponse.token !== 'undefined' &&
      this.authResponse.token !== null &&
      this.authResponse.token !== undefined &&
      this.authResponse.token.length > 0
    );
  }

  public isTwoFactorAuthenticationRequired(): Observable<boolean> {
    return this._twoFactorAuthenticationService.isRequired();
  }

  public logOff(): void {
    if (this.isAuthenticated()) {
      const logOffData: ITokenRequestData = {};

      this.adornKeyAndCodeFromStorage(logOffData);

      this._subscriptions.push(
        this.http
          .post(
            '/api/login/logout',
            {
              loginkey: decodeURIComponent(logOffData.loginKey),
              securitycode: decodeURIComponent(logOffData.securityCode),
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          )
          .pipe(
            finalize(() => {
              this.clearStorage();
              window.top.location.href = '/perfnav/logoff.asp';
            }),
            tap(() => {
              this.clearStorage();
              this.loggedIn$.next(false);
              window.top.location.href = '/perfnav/logoff.asp';
            })
          )
          .subscribe()
      );
    } else {
      this.clearStorage();
      if (window.location.pathname != '/login') {
        window.top.location.href = '/perfnav/logoff.asp';
      }
    }
  }

  public getDefaultUserRole(): Observable<number> {
    return this.http.post<number>('/api/auth/getdefaultrole', {});
  }

  public isManager(): Observable<ManagerType> {
    return this.http.post<ManagerType>('/api/auth/ismanager', {});
  }

  public getUserRoles(fetch = false): Observable<StudentRoleSettings> {
    return this._userService.getUserRoles(fetch).pipe(
      tap((e) => {
        const isEmployee = e.level === UserLevelEnum.Employee;
        const isLocalHr = [
          UserLevelEnum.LocalHr,
          UserLevelEnum.TrainingAdmin,
          UserLevelEnum.TrainingProvider,
          UserLevelEnum.InterimHr,
        ].includes(e.level);

        if (!isEmployee || (!e.isManager && !e.isMatrixManager)) {
          localStorage.removeItem('manager_view');
        }
        if (!isLocalHr || !e.localHrId) {
          localStorage.removeItem('localhr_view');
        }
        if (e.level !== UserLevelEnum.Trainer || !e.trainerId) {
          localStorage.removeItem('trainer_view');
        }
        if (e.level !== UserLevelEnum.CorporateHr || !e.corporateHrId) {
          localStorage.removeItem('corporatehr_view');
        }
        if (!isEmployee || !e.isMatrixManager) {
          localStorage.removeItem('matrixmanager_view');
        }
        if (!isEmployee) {
          localStorage.removeItem('delegate_view');
          localStorage.removeItem('delegate');
        }

        switch (e.level) {
          case UserLevelEnum.Trainer:
            if (!this._trainerViewActive()) {
              localStorage.setItem('trainer_view', '1');
            }
            break;
          case UserLevelEnum.LocalHr:
            if (!this._localHrViewActive()) {
              localStorage.setItem('localhr_view', '1');
            }
            break;
          case UserLevelEnum.CorporateHr:
            if (!this._corporateHrViewActive()) {
              localStorage.setItem('corporatehr_view', '1');
            }
            break;
          case UserLevelEnum.MatrixManager:
            if (!this._matrixManagerViewActive()) {
              localStorage.setItem('matrixmanager_view', '1');
              localStorage.setItem('manager_view', '1');
            }
            break;
        }
      })
    );
  }

  public changeToManager(override?: boolean): void {
    if (!override && this._managerViewActive() && !this._delegateViewActive()) {
      this._router.navigate(['/framework/manager-dashboard']);
      return;
    }
    localStorage.removeItem('manager_view');
    localStorage.removeItem('corporatehr_view');
    localStorage.removeItem('trainer_view');
    localStorage.removeItem('localhr_view');
    localStorage.removeItem('delegate_view');
    localStorage.removeItem('delegate');
    localStorage.removeItem('matrixmanager_view');

    this._subscriptions.push(
      this.switchUserRole(UserLevelEnum.Employee).subscribe(() => {
        localStorage.setItem('manager_view', '1');
        window.top.location.href = '/perfnav/student/ML/adm/V2contents.asp';
      })
    );
  }

  public changeToDelegate(team: DelegatedTeam): void {
    localStorage.removeItem('trainer_view');
    localStorage.removeItem('localhr_view');
    localStorage.removeItem('corporatehr_view');
    localStorage.removeItem('matrixmanager_view');

    this._subscriptions.push(
      this.switchUserRole(UserLevelEnum.Employee).subscribe(() => {
        localStorage.setItem('manager_view', '1');
        localStorage.setItem('delegate_view', '1');
        localStorage.setItem('delegate', JSON.stringify(team));
        window.top.location.href = '/perfnav/student/ML/adm/V2contents.asp';
      })
    );
  }

  public changeToCoupledEmployee(coupledEmployee: CoupledEmployee, asManager?: boolean) {
    localStorage.removeItem('trainer_view');
    localStorage.removeItem('localhr_view');
    localStorage.removeItem('manager_view');
    localStorage.removeItem('corporatehr_view');
    localStorage.removeItem('delegate_view');
    localStorage.removeItem('delegate');
    localStorage.removeItem('matrixmanager_view');

    this._subscriptions.push(
      this.switchUserRole(
        UserLevelEnum.Employee,
        coupledEmployee.studentId
      ).subscribe(() => {
        if (asManager) {
          localStorage.setItem('manager_view', '1');
        }

        window.top.location.href = '/perfnav/student/ML/adm/V2contents.asp';
      })
    );
  }

  public changeToTrainer(override?: boolean): void {
    localStorage.removeItem('corporatehr_view');
    localStorage.removeItem('manager_view');
    localStorage.removeItem('localhr_view');
    localStorage.removeItem('delegate_view');
    localStorage.removeItem('delegate');
    localStorage.removeItem('matrixmanager_view');

    this._subscriptions.push(
      this.switchUserRole(UserLevelEnum.Trainer).subscribe(() => {
        localStorage.setItem('trainer_view', '1');
        window.top.location.href = '/perfnav/student/ML/adm/V2contents.asp';
      })
    );
  }

  public changeToMatrixManager(override?: boolean): void {
    localStorage.removeItem('trainer_view');
    localStorage.removeItem('localhr_view');
    localStorage.removeItem('delegate_view');
    localStorage.removeItem('delegate');
    localStorage.removeItem('corporatehr_view');

    this._subscriptions.push(
      this.switchUserRole(UserLevelEnum.Employee).subscribe(() => {
        localStorage.setItem('manager_view', '1');
        localStorage.setItem('matrixmanager_view', '1');
        window.top.location.href = '/perfnav/student/ML/adm/V2contents.asp';
      })
    );
  }

  public changeToLocalHr(override?: boolean): void {
    localStorage.removeItem('manager_view');
    localStorage.removeItem('trainer_view');
    localStorage.removeItem('delegate_view');
    localStorage.removeItem('delegate');
    localStorage.removeItem('corporatehr_view');
    localStorage.removeItem('matrixmanager_view');

    this._subscriptions.push(
      this.switchUserRole(UserLevelEnum.LocalHr).subscribe(() => {
        localStorage.setItem('localhr_view', '1');
        window.top.location.href =
          '/perfnav/localhr/en/adm/contents.asp?level=1&menuitem=1&indirrep=';
      })
    );
  }

  public changeToCorporateHr(override?: boolean): void {
    localStorage.removeItem('manager_view');
    localStorage.removeItem('trainer_view');
    localStorage.removeItem('delegate_view');
    localStorage.removeItem('delegate');
    localStorage.removeItem('localhr_view');
    localStorage.removeItem('matrixmanager_view');

    this._subscriptions.push(
      this.switchUserRole(UserLevelEnum.CorporateHr).subscribe(() => {
        localStorage.setItem('corporatehr_view', '1');
        window.top.location.href =
          '/perfnav/administration/en/adm/V2contents.asp?level=1&menuitem=1&indirrep=';
      })
    );
  }

  public changeToEmployee(): void {
    if (
      !this._trainerViewActive() &&
      !this._localHrViewActive() &&
      !this._corporateHrViewActive() &&
      !this._corporateHrViewActive() &&
      !this._managerViewActive() &&
      !this._delegateViewActive() &&
      !this._matrixManagerViewActive()
    ) {
      this._router.navigate(['/framework/ess']);
      return;
    }
    this._subscriptions.push(
      this.switchUserRole(UserLevelEnum.Employee).subscribe(() => {
        localStorage.removeItem('manager_view');
        localStorage.removeItem('corporatehr_view');
        localStorage.removeItem('trainer_view');
        localStorage.removeItem('localhr_view');
        localStorage.removeItem('delegate_view');
        localStorage.removeItem('delegate');
        localStorage.removeItem('matrixmanager_view');
        window.top.location.href = '/perfnav/student/ML/adm/V2contents.asp';
      })
    );
  }

  private switchUserRole(
    role: UserLevelEnum,
    coupledStudentId?: number
  ): Observable<any> {
    const tokenData: ITokenRequestData = {
      loginKey: null,
      securityCode: null,
    };
    this.adornKeyAndCodeFromStorage(tokenData);

    return this._userService
      .switchUserRole(role, tokenData, coupledStudentId)
      .pipe(
        tap(() => {
          localStorage.removeItem(this.tokenStorageKey);
          this.authResponse = undefined;
        })
      );
  }

  public managerViewActive(): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(() => {
        return localStorage.getItem('manager_view') === '1';
      })
    );
  }

  public trainerViewActive(): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(() => {
        return localStorage.getItem('trainer_view') === '1';
      })
    );
  }

  public matrixManagerViewActive(): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(() => {
        return localStorage.getItem('matrixmanager_view') === '1';
      })
    );
  }

  public localHrViewActive(): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(() => {
        return localStorage.getItem('localhr_view') === '1';
      })
    );
  }

  public corporateHrViewActive(): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(() => {
        return localStorage.getItem('corporatehr_view') === '1';
      })
    );
  }

  public getActiveDelegation(): DelegatedTeam {
    return this._delegateViewActive();
  }

  private _managerViewActive(): boolean {
    return localStorage.getItem('manager_view') === '1';
  }

  private _trainerViewActive(): boolean {
    return localStorage.getItem('trainer_view') === '1';
  }

  private _localHrViewActive(): boolean {
    return localStorage.getItem('localhr_view') === '1';
  }

  private _corporateHrViewActive(): boolean {
    return localStorage.getItem('corporatehr_view') === '1';
  }

  private _matrixManagerViewActive(): boolean {
    return localStorage.getItem('matrixmanager_view') === '1';
  }

  private _delegateViewActive(): DelegatedTeam {
    const delegate = localStorage.getItem('delegate');
    if (localStorage.getItem('delegate_view') === '1' && delegate && delegate.length) {
      try {
        return JSON.parse(delegate);
      } catch {
        localStorage.removeItem('delegate_view');
        localStorage.removeItem('delegate');
        return null;
      }
    }

    if (localStorage.getItem('delegate_view') === '1') {
      localStorage.removeItem('delegate_view');
      localStorage.removeItem('delegate');
    }
  }

  private adornKeyAndCodeFromStorage(
    tokenRequestData: ITokenRequestData
  ): ITokenRequestData {
    const loginKey = localStorage.getItem('Progreso.loginKey');

    if (typeof loginKey !== 'undefined' && loginKey) {
      tokenRequestData.loginKey = loginKey;
    }

    const securityCode = localStorage.getItem('Progreso.securityCode');

    if (typeof securityCode !== 'undefined' && securityCode) {
      tokenRequestData.securityCode = securityCode;
    }

    return tokenRequestData;
  }

  private tokenRequestMatchesCurrentCredentials(
    tokenRequestData: ITokenRequestData
  ): boolean {
    return localStorage.getItem('Progreso.loginKey') === tokenRequestData.loginKey &&
      localStorage.getItem('Progreso.securityCode') === tokenRequestData.securityCode;
  }

  private setTokenInStorageAndMemory(token: AuthorizationBody): void {
    if (typeof token !== 'undefined' && token) {
      if (
        token.hasOwnProperty('token') &&
        typeof token.token !== 'undefined' &&
        token.token
      ) {
        localStorage.setItem(this.tokenStorageKey, token.token);
        this.authResponse.token = token.token;
      }
    }
  }

  /**
   *  Retrieves the localstorage value for Progreso.authorizationData and places it in this.token
   */
  private getTokenFromStorageAndSetToMemory(): void {
    this.authResponse = new AuthorizationBody();

    const tokenStorage = localStorage.getItem(this.tokenStorageKey);

    if (typeof tokenStorage !== 'undefined' && tokenStorage) {
      this.authResponse.token = tokenStorage;
    } else {
      this.authResponse = undefined;
    }
  }

  public clearStorage(): void {
    localStorage.removeItem(this.tokenStorageKey);
    localStorage.removeItem('Progreso.loginKey');
    localStorage.removeItem('Progreso.securityCode');
    localStorage.clear();
    sessionStorage.clear();
    this._endPointService.clearCache();
    this.authResponse = undefined;
    this.loggedIn$.next(false);
  }

  private getAuthorizationToken(
    tokenData: ITokenRequestData
  ): Observable<AuthorizationBody> {
    return this.http
      .post<AuthorizationBody>('/api/login/token', tokenData, {
        headers: {
          'Request-Type': 'authentication',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': [
            'GET',
            'POST',
            'OPTIONS',
            'PUT',
            'DELETE',
          ],
          'Access-Control-Allow-Headers': 'Content-Type',
          'Access-Control-Allow-Credentials': 'true',
        },
      })
      .pipe(catchError((error) => this.errorHandler(error)));
  }

  private keepAlive(): Observable<void> {
    if (this.isPublicRoute()) {
      return of(void 0);
    }

    const url = window.location.origin + '/PerfNav/ping.asp';
    const classicPing = this.http.get(url, {
      responseType: 'text',
      observe: 'response'  // get the full response so we can inspect the final URL
    }).pipe(
      tap(response => {
        // After following the 302, the final URL will be the timeout/login page
        if (response.url?.toLowerCase().includes('timeout') ||
          response.url?.toLowerCase().includes('login')) {
          if (!this.isPublicRoute()) {
            this._router.navigate(['/login']);
          }
        }
      })
    );

    const tokenData: ITokenRequestData = {
      loginKey: null,
      securityCode: null,
    };
    this.adornKeyAndCodeFromStorage(tokenData);
    const dotnetPing = this.http.post('/api/auth/ping', tokenData);
    return forkJoin([classicPing, dotnetPing]).pipe(map(() => void 0));
  }

  private isPublicRoute(): boolean {
    const currentUrl = window.location.href.toLowerCase();

    return currentUrl.indexOf('login') > -1 ||
      currentUrl.indexOf('passwordforget') > -1 ||
      currentUrl.indexOf('resetpassword') > -1 ||
      currentUrl.indexOf('pagepublic') > -1 ||
      currentUrl.indexOf('perfnav/logon/') > -1 ||
      currentUrl.indexOf('rs/recruitmenttechnologies/jobdetail') > -1 ||
      currentUrl.indexOf('loginforget') > -1 ||
      currentUrl.indexOf('candidate-login') > -1 ||
      this.publicEmbeddedRouteFragments.some(fragment => currentUrl.indexOf(fragment) > -1);
  }

  private errorHandler(error: HttpErrorResponse): Observable<never> {
    console.error(error || 'Server Error');
    if (error.message == 'invalid_client') {
      window.top.location.href = '/login';
    }
    if (!isDevMode()) {
      this._router.navigate(['/error'], {
        queryParams: { code: error && error.status ? error.status : 500 },
      });
    }

    return throwError(() => error);
  }

  ngOnDestroy(): void {
    for (const subscription of this._subscriptions) {
      subscription.unsubscribe();
    }
  }
}
